import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { SeasonPlayerRecord } from '../api/types.js';
import { useAppState } from './AppState.js';
import { ErrorPanel, LoadingPanel, PageIntro } from './components.js';
import { mapError } from './errors.js';
import { useInvalidateQueries, useRequiredLeagueId, useRequiredMatchDayId, useSyncLeagueFromRoute } from './hooks.js';

function badge(status: string) {
  return <span className={`badge ${status || 'unknown'}`}>{status || 'unknown'}</span>;
}

export function AdminMatchDayPage() {
  const leagueId = useRequiredLeagueId();
  const matchDayId = useRequiredMatchDayId();
  useSyncLeagueFromRoute(leagueId);
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();

  const matchDayQuery = useQuery({
    queryKey: ['matchDay', leagueId, matchDayId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getMatchDay(leagueId, matchDayId)).matchDay,
  });
  const matchesQuery = useQuery({
    queryKey: ['matchDayMatches', leagueId, matchDayId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getMatchDayMatches(leagueId, matchDayId)).matches,
  });
  const seasonsQuery = useQuery({
    queryKey: ['seasons', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getSeasons(leagueId)).seasons,
  });
  const coursesQuery = useQuery({
    queryKey: ['courses', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getCourses(leagueId)).courses,
  });
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, matchDayQuery.data?.season_id ?? null],
    enabled: Boolean(client && matchDayQuery.data?.season_id),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, matchDayQuery.data!.season_id)).seasonPlayers,
  });

  const [matchDrafts, setMatchDrafts] = useState<Array<{ playerAId: string; playerBId: string }>>([]);

  useEffect(() => {
    if (matchesQuery.data) {
      setMatchDrafts(matchesQuery.data.map((match) => ({ playerAId: match.player_a_id, playerBId: match.player_b_id })));
    }
  }, [matchesQuery.data]);

  const replaceMutation = useMutation({
    mutationFn: async () => {
      const seen = new Set<string>();
      for (const match of matchDrafts) {
        if (!match.playerAId || !match.playerBId) {
          throw new Error('Finish both players for each match row or remove incomplete rows before saving.');
        }
        if (match.playerAId === match.playerBId) {
          throw new Error('Player A and Player B must be different.');
        }
        for (const playerId of [match.playerAId, match.playerBId]) {
          if (seen.has(playerId)) {
            throw new Error('A player cannot appear in multiple matches.');
          }
          seen.add(playerId);
        }
      }
      return client!.replaceMatchDayMatches(leagueId, matchDayId, { matches: matchDrafts });
    },
    onSuccess: async () => {
      setBanner('success', 'Matchups saved successfully.');
      await invalidate(['matchDayMatches', leagueId, matchDayId], ['matchDays', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const lockMutation = useMutation({
    mutationFn: async () => client!.updateMatchDay(leagueId, matchDayId, { status: 'locked' }),
    onSuccess: async () => {
      setBanner('success', 'Match day locked.');
      await invalidate(['matchDay', leagueId, matchDayId], ['matchDays', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (
    matchDayQuery.isLoading ||
    matchesQuery.isLoading ||
    seasonsQuery.isLoading ||
    coursesQuery.isLoading ||
    seasonPlayersQuery.isLoading
  ) {
    return <LoadingPanel title="Match Day Setup" message="Loading match-day setup." />;
  }

  if (
    matchDayQuery.isError ||
    matchesQuery.isError ||
    seasonsQuery.isError ||
    coursesQuery.isError ||
    seasonPlayersQuery.isError ||
    !matchDayQuery.data ||
    !matchesQuery.data ||
    !coursesQuery.data
  ) {
    return (
      <ErrorPanel
        title="Match Day Setup"
        message={mapError(
          matchDayQuery.error ??
            matchesQuery.error ??
            seasonsQuery.error ??
            coursesQuery.error ??
            seasonPlayersQuery.error,
        )}
      />
    );
  }

  const matchDay = matchDayQuery.data;
  const matches = matchesQuery.data;
  const seasons = seasonsQuery.data ?? [];
  const courses = coursesQuery.data;
  const seasonPlayers = seasonPlayersQuery.data ?? [];
  const course = courses.find((candidate) => candidate.id === matchDay.course_id) ?? null;
  const seasonName = seasons.find((candidate) => candidate.id === matchDay.season_id)?.name || 'Unknown season';
  const isLocked = matchDay.status === 'locked';
  const allMatchesCompleted = matches.length > 0 && matches.every((match) => match.status === 'completed');
  const canLockMatchDay = !isLocked && allMatchesCompleted;

  return (
    <>
      <PageIntro
        title={`Match Day Setup ${matchDay.date}`}
        actions={
          <Link className="nav-link" to={`/leagues/${leagueId}/admin/score-entry?matchDayId=${matchDayId}`}>
            Open Score Entry
          </Link>
        }
      />
      <section className="grid">
        <article className="panel stack">
          <p>
            <strong>Status:</strong> {badge(matchDay.status)}
          </p>
          <p>
            <strong>Season:</strong> {seasonName}
          </p>
          <p>
            <strong>Course:</strong> {course?.name || 'TBD'}
          </p>
          <p>
            <strong>Hammer Hole:</strong> {matchDay.hammer_hole ? `Hole ${matchDay.hammer_hole}` : 'None'}
          </p>
          <p>
            <strong>Matches:</strong> {matches.length}
          </p>
          {canLockMatchDay ? (
            <p className="alert warn">
              Scores are complete. Lock this week to freeze scoring and handicaps before the next match day.
            </p>
          ) : null}

          <h3>Manage Matchups</h3>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (isLocked) {
                return;
              }
              void replaceMutation.mutateAsync();
            }}
          >
            <div className="stack">
              {matchDrafts.length === 0 ? <p className="muted">No matches yet. Click "Add Match" to create one.</p> : null}
              {matchDrafts.map((match, index) => (
                <PairingEditorRow
                  key={index}
                  match={match}
                  seasonPlayers={seasonPlayers}
                  selected={matchDrafts}
                  disabled={isLocked}
                  onChange={(next) =>
                    setMatchDrafts((current) => current.map((entry, entryIndex) => (entryIndex === index ? next : entry)))
                  }
                  onRemove={() =>
                    setMatchDrafts((current) => current.filter((_entry, entryIndex) => entryIndex !== index))
                  }
                />
              ))}
            </div>
            <div className="inline-actions">
              <button
                type="button"
                disabled={isLocked}
                onClick={() =>
                  setMatchDrafts((current) => [...current, { playerAId: '', playerBId: '' }])
                }
              >
                Add Match
              </button>
              <button type="submit" disabled={isLocked || replaceMutation.isPending}>
                Save All Matchups
              </button>
              {canLockMatchDay ? (
                <button type="button" className="secondary" onClick={() => void lockMutation.mutateAsync()}>
                  Lock Match Day
                </button>
              ) : null}
            </div>
            {isLocked ? (
              <p className="alert warn">Match day is locked; matchup edits are disabled.</p>
            ) : null}
          </form>
        </article>
      </section>
    </>
  );
}

function PairingEditorRow({
  match,
  seasonPlayers,
  selected,
  disabled,
  onChange,
  onRemove,
}: {
  match: { playerAId: string; playerBId: string };
  seasonPlayers: SeasonPlayerRecord[];
  selected: Array<{ playerAId: string; playerBId: string }>;
  disabled: boolean;
  onChange: (match: { playerAId: string; playerBId: string }) => void;
  onRemove: () => void;
}) {
  const selectedIds = useMemo(
    () => new Set(selected.flatMap((entry) => [entry.playerAId, entry.playerBId]).filter(Boolean)),
    [selected],
  );

  const optionsFor = (currentValue: string) =>
    seasonPlayers.filter(
      (seasonPlayer) => !selectedIds.has(seasonPlayer.player_id) || seasonPlayer.player_id === currentValue,
    );

  return (
    <div className="card">
      <div className="row" style={{ alignItems: 'end', gap: '1rem' }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Player A</label>
          <select
            disabled={disabled}
            value={match.playerAId}
            onChange={(event) => onChange({ ...match, playerAId: event.target.value })}
          >
            <option value="">Select Player A</option>
            {optionsFor(match.playerAId).map((seasonPlayer) => (
              <option key={seasonPlayer.player_id} value={seasonPlayer.player_id}>
                {seasonPlayer.player?.name || seasonPlayer.player_id}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Player B</label>
          <select
            disabled={disabled}
            value={match.playerBId}
            onChange={(event) => onChange({ ...match, playerBId: event.target.value })}
          >
            <option value="">Select Player B</option>
            {optionsFor(match.playerBId).map((seasonPlayer) => (
              <option key={seasonPlayer.player_id} value={seasonPlayer.player_id}>
                {seasonPlayer.player?.name || seasonPlayer.player_id}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="danger" disabled={disabled} onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  );
}
